import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, CheckCircle, XCircle, Edit, RotateCcw, Save, User, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTradeApps() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/admin/trade-applications${params}`);
      setApplications(res.data);
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const reviewApplication = async (appId, status, tier = null) => {
    try {
      await axios.put(`${API}/admin/trade-applications/${appId}`, {
        status,
        assigned_tier: tier,
      });
      toast.success(`Application ${status}`);
      fetchApplications();
      setDialogOpen(false);
    } catch (err) {
      toast.error('Failed to update application');
    }
  };

  const saveEdits = async () => {
    try {
      await axios.patch(`${API}/admin/trade-applications/${selectedApp.id}`, editForm);
      toast.success('Application updated');
      fetchApplications();
      setEditMode(false);
      // Refresh selected app
      const updated = { ...selectedApp, ...editForm };
      setSelectedApp(updated);
    } catch (err) {
      toast.error('Failed to save changes');
    }
  };

  const deleteApplication = async (app) => {
    if (!window.confirm(`Delete trade application from ${app.business_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/trade-applications/${app.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success('Application deleted');
      fetchApplications();
      if (selectedApp?.id === app.id) setSelectedApp(null);
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openViewDialog = (app) => {
    setSelectedApp(app);
    setEditForm({
      business_name: app.business_name || '',
      abn: app.abn || '',
      contact_name: app.contact_name || '',
      email: app.email || '',
      phone: app.phone || '',
      website: app.website || '',
      billing_address: app.billing_address || '',
      shipping_address: app.shipping_address || '',
      notes: app.notes || '',
      admin_notes: app.admin_notes || '',
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'approved': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'rejected': return 'bg-red-500/20 text-red-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'trade': return 'bg-blue-500/20 text-blue-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-trade-apps">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Trade Applications
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Approved applications automatically update user pricing tiers
            </p>
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <User className="h-5 w-5 text-blue-500 mt-0.5" />
            <div>
              <p className="text-blue-400 font-medium">User Account Updates</p>
              <p className="text-blue-300/70 text-sm">
                When you approve an application, the user's account is automatically upgraded to Trade pricing tier. 
                They will immediately see the discounted prices when logged in.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Business</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">ABN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Submitted</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-4">
                      <div className="h-8 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    No applications found
                  </td>
                </tr>
              ) : (
                applications.map(app => (
                  <tr key={app.id} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-4">
                      <p className="text-white font-medium">{app.business_name}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white">{app.contact_name}</p>
                      <p className="text-xs text-zinc-500">{app.email}</p>
                    </td>
                    <td className="px-4 py-4 text-zinc-400 font-mono">{app.abn}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(app.status)}>{app.status}</Badge>
                        {app.assigned_tier && (
                          <Badge className={getTierColor(app.assigned_tier)}>{app.assigned_tier}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-zinc-400 text-sm">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openViewDialog(app)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteApplication(app)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`delete-trade-app-${app.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Application Detail/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditMode(false); }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-white">
                  {editMode ? 'Edit Trade Application' : 'Trade Application'}
                </DialogTitle>
                {!editMode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditMode(true)}
                    className="text-[#78BE20] hover:text-[#78BE20]/80"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
            </DialogHeader>
            {selectedApp && (
              <div className="space-y-4 mt-4">
                {editMode ? (
                  /* Edit Mode */
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Business Name</Label>
                        <Input
                          value={editForm.business_name}
                          onChange={(e) => setEditForm({...editForm, business_name: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>ABN</Label>
                        <Input
                          value={editForm.abn}
                          onChange={(e) => setEditForm({...editForm, abn: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>Contact Name</Label>
                        <Input
                          value={editForm.contact_name}
                          onChange={(e) => setEditForm({...editForm, contact_name: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={editForm.phone}
                          onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input
                          value={editForm.website}
                          onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                          className="mt-1 bg-zinc-800 border-zinc-700"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Billing Address</Label>
                      <Textarea
                        value={editForm.billing_address}
                        onChange={(e) => setEditForm({...editForm, billing_address: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700"
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Shipping Address</Label>
                      <Textarea
                        value={editForm.shipping_address}
                        onChange={(e) => setEditForm({...editForm, shipping_address: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700"
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Applicant Notes</Label>
                      <Textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700"
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Admin Notes (Internal)</Label>
                      <Textarea
                        value={editForm.admin_notes}
                        onChange={(e) => setEditForm({...editForm, admin_notes: e.target.value})}
                        className="mt-1 bg-zinc-800 border-zinc-700"
                        rows={2}
                        placeholder="Internal notes about this application..."
                      />
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-zinc-800">
                      <Button
                        variant="outline"
                        className="flex-1 border-zinc-700"
                        onClick={() => setEditMode(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]"
                        onClick={saveEdits}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </Button>
                    </div>
                  </>
                ) : (
                  /* View Mode */
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <Badge className={getStatusColor(selectedApp.status)}>{selectedApp.status}</Badge>
                      {selectedApp.assigned_tier && (
                        <Badge className={getTierColor(selectedApp.assigned_tier)}>{selectedApp.assigned_tier} pricing</Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-zinc-400 text-sm">Business Name</p>
                        <p className="text-white">{selectedApp.business_name}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">ABN</p>
                        <p className="text-white font-mono">{selectedApp.abn}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">Contact</p>
                        <p className="text-white">{selectedApp.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">Email</p>
                        <p className="text-white">{selectedApp.email}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">Phone</p>
                        <p className="text-white">{selectedApp.phone}</p>
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm">Website</p>
                        <p className="text-white">{selectedApp.website || 'N/A'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-zinc-400 text-sm">Billing Address</p>
                      <p className="text-white whitespace-pre-wrap">{selectedApp.billing_address}</p>
                    </div>

                    <div>
                      <p className="text-zinc-400 text-sm">Shipping Address</p>
                      <p className="text-white whitespace-pre-wrap">{selectedApp.shipping_address}</p>
                    </div>

                    {selectedApp.notes && (
                      <div>
                        <p className="text-zinc-400 text-sm">Applicant Notes</p>
                        <p className="text-white whitespace-pre-wrap">{selectedApp.notes}</p>
                      </div>
                    )}

                    {selectedApp.admin_notes && (
                      <div className="bg-zinc-800 p-3 rounded">
                        <p className="text-zinc-400 text-sm">Admin Notes</p>
                        <p className="text-yellow-400 whitespace-pre-wrap">{selectedApp.admin_notes}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-4 border-t border-zinc-800 space-y-3">
                      {selectedApp.status === 'pending' ? (
                        <div className="flex gap-3">
                          <Button
                            onClick={() => reviewApplication(selectedApp.id, 'approved', 'trade')}
                            className="flex-1 bg-[#78BE20] text-black hover:bg-[#65a31b]"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve as Trade
                          </Button>
                          <Button
                            onClick={() => reviewApplication(selectedApp.id, 'rejected')}
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-500/10"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-zinc-400 text-sm">Change Status:</p>
                          <div className="flex gap-3">
                            <Button
                              onClick={() => reviewApplication(selectedApp.id, 'pending')}
                              variant="outline"
                              className="flex-1 border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                            >
                              <RotateCcw className="h-4 w-4 mr-2" />
                              Reset to Pending
                            </Button>
                            {selectedApp.status !== 'approved' && (
                              <>
                                <Button
                                  onClick={() => reviewApplication(selectedApp.id, 'approved', 'trade')}
                                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                                >
                                  Approve Trade
                                </Button>
                              </>
                            )}
                            {selectedApp.status !== 'rejected' && (
                              <Button
                                onClick={() => reviewApplication(selectedApp.id, 'rejected')}
                                variant="outline"
                                className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10"
                              >
                                Reject
                              </Button>
                            )}
                          </div>
                          <Button
                            onClick={() => { deleteApplication(selectedApp); setDialogOpen(false); }}
                            variant="outline"
                            className="w-full mt-3 border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid="delete-trade-app-modal-btn"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Application
                          </Button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
